// web/src/features/admin/SystemConfigDev.jsx
import React from 'react'
import IdentitySettings from './IdentitySettings.jsx'
import { getSysConfig, saveSysConfig } from '../../lib/devStore'
import AdminBar from '../../components/AdminBar.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import { subscribeSysConfigFS, saveSysConfigFS } from '@/services/firestore/sysConfigService'

// Firestore facility types service
import {
  refreshFacilityTypes,
  addFacilityType,
  softDeleteFacilityType,
  restoreFacilityType
} from '@/services/firestore/facilityTypeService'

// Firestore approval limits service
import {
  listApprovalLimitsFS,
  replaceAllApprovalLimitsFS
} from '@/services/firestore/approvalLimitsService'

// Firestore doc/file types service
import {
  listDocTypesFS,
  addDocTypeFS,
  softDeleteDocTypeFS,
  restoreDocTypeFS
} from '@/services/firestore/docTypesService'

// Firestore helpers
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  writeBatch
} from 'firebase/firestore'
import { app as firebaseApp, serverTimestamp } from '@/firebase'

export default function SystemConfigDev() {
  // ---------- Config state (normalized) ----------
  const [cfg, setCfg] = React.useState(() => {
    const raw = getSysConfig() || {}

    const uploads = {
      maxPdfMB: Math.max(
        1,
        Number(raw?.uploads?.maxPdfMB ?? 25) || 25
      )
    }

const entrata = {
  ...(raw.entrata || {}),
  // If there's no stored value yet, default to TRUE
  enableAutoPmSync: raw?.entrata?.enableAutoPmSync ?? true
}

    return {
      ...raw,
      uploads,
      entrata
    }
  })

  const [dirty, setDirty] = React.useState(false)
  const [cfgSaving, setCfgSaving] = React.useState(false)
  const [cfgMsg, setCfgMsg] = React.useState('')
  const [adminOnlyArchive, setAdminOnlyArchive] = React.useState(false)
  const [archiveSaving, setArchiveSaving] = React.useState(false)
  const [archiveMsg, setArchiveMsg] = React.useState('')

  // Facility types
  const [ftLoading, setFtLoading] = React.useState(true)
  const [ftErr, setFtErr] = React.useState('')
  const [types, setTypes] = React.useState([])
  const [newType, setNewType] = React.useState('')

  // Approval limits
  const [staged, setStaged] = React.useState([])
  const [liveLimits, setLiveLimits] = React.useState([])
  const [publishing, setPublishing] = React.useState(false)
  const [pubMsg, setPubMsg] = React.useState('')

  // Editing states
  const [editingId, setEditingId] = React.useState('')
  const [editingLimit, setEditingLimit] = React.useState('')

  // Role dropdown
  const [roleOptions, setRoleOptions] = React.useState([])
  const [roleSel, setRoleSel] = React.useState('')
  const [limitInput, setLimitInput] = React.useState('')

  // Confirm dialog (shared)
  const [confirm, setConfirm] = React.useState({
    open: false,
    kind: null,
    id: '',
    label: ''
  })

  // Document/File types state
  const [dtLoading, setDtLoading] = React.useState(true)
  const [dtErr, setDtErr] = React.useState('')
  const [docTypes, setDocTypes] = React.useState([])
  const [newDocType, setNewDocType] = React.useState('')

  // Entrata PM sync UI state
  const [pmSyncRunning, setPmSyncRunning] = React.useState(false)
  const [pmSyncMsg, setPmSyncMsg] = React.useState('')
  const [pmSyncLogs, setPmSyncLogs] = React.useState([])
  const [pmSyncLogsLoading, setPmSyncLogsLoading] = React.useState(false)
  const [pmSyncLogsError, setPmSyncLogsError] = React.useState('')
  const [pmSyncLogsOpen, setPmSyncLogsOpen] = React.useState(false)
  const [showPmWorkOrderMapping, setShowPmWorkOrderMapping] = React.useState(false)

  const db = getFirestore(firebaseApp)

  // ---------- Helpers ----------
  function setField(path, val) {
    setCfg(prev => {
      const base = prev || {}
      const next = { ...base }
      const seg = path.split('.')
      let cur = next
      for (let i = 0; i < seg.length - 1; i++) {
        const k = seg[i]
        cur[k] = cur[k] == null ? {} : { ...cur[k] }
        cur = cur[k]
      }
      cur[seg[seg.length - 1]] = val

      // keep uploads normalized
      next.uploads = {
        maxPdfMB: Math.max(
          1,
          Number(next?.uploads?.maxPdfMB ?? 25) || 25
        )
      }

      return next
    })
    setDirty(true)
  }

  const fmtTS = ts => {
    if (!ts) return '—'
    // Firestore Timestamp (client SDK) or serialized from admin { _seconds, _nanoseconds }
    if (ts.seconds || ts._seconds) {
      const secs = ts.seconds ?? ts._seconds
      return new Date(secs * 1000).toLocaleString()
    }
    const d = new Date(ts)
    return isNaN(d) ? '—' : d.toLocaleString()
  }

  function handleToggleAutoPmSync(e) {
  const checked = e.target.checked;

  setCfg(prev => {
    const base = prev || {};

    const next = {
      ...base,
      entrata: {
        ...(base.entrata || {}),
        enableAutoPmSync: checked
      },
      uploads: {
        maxPdfMB: Math.max(
          1,
          Number(base?.uploads?.maxPdfMB ?? 25) || 25
        )
      }
    };

    try {
      // saveSysConfig is synchronous — DO NOT use .catch()
      saveSysConfig(next);
    } catch (err) {
      console.error('Failed to save enableAutoPmSync:', err);
    }

    return next;
  });

  // This auto-save should NOT dirty the Uploads card
}

  async function handleToggleShowPmWorkOrderMapping(e) {
    const checked = e.target.checked === true
    // update UI immediately
    setShowPmWorkOrderMapping(checked)
    setPmSyncMsg('Saving…')
    try {
      await saveSysConfigFS({
        entrata: {
          showPmWorkOrderMapping: checked,
        },
      })
      setPmSyncMsg('Saved.')
      setTimeout(() => setPmSyncMsg(''), 1500)
    } catch (err) {
      console.error('Failed to save showPmWorkOrderMapping:', err)
      setPmSyncMsg('Save failed.')
    }
  }

  // ---------- Save dev cfg (Uploads card) ----------
  async function saveDevCfg() {
    setCfgSaving(true)
    setCfgMsg('')
    try {
      const sanitized = {
        ...cfg,
        uploads: {
          maxPdfMB: Math.max(
            1,
            Number(cfg?.uploads?.maxPdfMB ?? 25) || 25
          )
        }
      }
      await saveSysConfig(sanitized)
      setCfg(sanitized)
      setDirty(false)
      setCfgMsg('Saved.')
      setTimeout(() => setCfgMsg(''), 1500)
    } catch (e) {
      setCfgMsg(e.message || 'Save failed.')
    } finally {
      setCfgSaving(false)
    }
  }

  async function saveArchiveRestriction() {
    setArchiveSaving(true)
    setArchiveMsg('')
    try {
      await saveSysConfigFS({
        restrictions: {
          adminOnlyArchive: adminOnlyArchive === true,
        },
      })
      setArchiveMsg('Saved.')
      setTimeout(() => setArchiveMsg(''), 1500)
    } catch (e) {
      setArchiveMsg(e?.message || 'Save failed.')
    } finally {
      setArchiveSaving(false)
    }
  }

  // ---------- Facility Types ----------
  async function loadFacilityTypes() {
    setFtErr('')
    setFtLoading(true)
    try {
      const rows = await refreshFacilityTypes()
      setTypes(rows)
    } catch (e) {
      console.error(e)
      setFtErr(e.message || 'Failed to load facility types.')
    } finally {
      setFtLoading(false)
    }
  }

  async function onAddType(e) {
    e?.preventDefault?.()
    const t = newType.trim()
    if (!t) return
    try {
      await addFacilityType(t)
      setNewType('')
      await loadFacilityTypes()
    } catch (e) {
      setFtErr(e.message || 'Failed to add type.')
    }
  }

  function askDeleteType(row) {
    setConfirm({ open: true, kind: 'ft-delete', id: row.id, label: row.name })
  }

  function askRestoreType(row) {
    setConfirm({ open: true, kind: 'ft-restore', id: row.id, label: row.name })
  }

  // ---------- Roles (for approval limits) ----------
  function uniqSorted(list) {
    const seen = new Set()
    const out = []
    const banned = [
      'admin',
      'approver',
      'sysconfig',
      'viewer',
      'requester',
      'owner',
      'all',
      'operations admin',
      '3p',
      'developer',
      'superuser'
    ]
    for (const x of list) {
      const k = (x || '').toString().trim()
      if (!k) continue
      const low = k.toLowerCase()
      if (banned.includes(low)) continue
      if (!seen.has(low)) {
        seen.add(low)
        out.push(k)
      }
    }
    out.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    return out
  }

  async function loadRoleOptions() {
    try {
      const snap = await getDocs(collection(db, 'users'))
      const dyn = []
      for (const d of snap.docs) {
        const u = d.data() || {}
        if (u.role) dyn.push(String(u.role))
        if (Array.isArray(u.roles))
          for (const r of u.roles) dyn.push(String(r))
        if (u.title) dyn.push(String(u.title))
      }
      const traditional = [
        'Manager',
        'Regional Manager',
        'VP / Facilities',
        'VP / Operations'
      ]
      const options = uniqSorted([...traditional, ...dyn])
      setRoleOptions(options)
      if (!roleSel && options.length) setRoleSel(options[0])
    } catch {
      const fallback = [
        'Manager',
        'Regional Manager',
        'VP / Facilities',
        'VP / Operations'
      ]
      setRoleOptions(fallback)
      if (!roleSel) setRoleSel(fallback[0])
    }
  }

  // ---------- Approval Limits ----------
  async function loadLiveLimits() {
    try {
      const rows = await listApprovalLimitsFS()
      setLiveLimits(rows)
    } catch {
      const snap = await getDocs(collection(db, 'approvalLimits'))
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      setLiveLimits(rows)
    }
  }

  function addStaged() {
    const role = roleSel.trim()
    if (!role) return
    const raw = limitInput.trim()
    if (!raw) return
    const isUnlimited = raw.toLowerCase() === 'unlimited'
    const parsed = isUnlimited ? 'unlimited' : Number(raw.replace(/[, ]+/g, ''))
    if (!isUnlimited && (isNaN(parsed) || parsed < 0)) {
      alert('Enter a positive number or "unlimited".')
      return
    }
    setStaged(prev => {
      const next = prev.filter(
        r => (r.role || '').toLowerCase() !== role.toLowerCase()
      )
      next.push({ role, limit: parsed })
      next.sort((a, b) => {
        const av = a.limit === 'unlimited' ? Infinity : a.limit
        const bv = b.limit === 'unlimited' ? Infinity : b.limit
        return av - bv
      })
      return next
    })
    setLimitInput('')
  }

  async function publishApprovalLimits() {
    if (staged.length === 0) {
      alert('Add at least one row before publishing.')
      return
    }
    setPublishing(true)
    setPubMsg('')
    try {
      const stagedKey = r =>
        String(r.role || '')
          .trim()
          .toLowerCase()
      const stagedMap = new Map(staged.map(r => [stagedKey(r), r]))

      const keep = (liveLimits || [])
        .filter(r => r.isActive)
        .filter(r => !stagedMap.has(stagedKey(r)))
        .map(r => ({ role: r.role, limit: r.limit, isActive: true }))

      const merged = [
        ...keep,
        ...staged.map(r => ({ role: r.role, limit: r.limit, isActive: true }))
      ]

      merged.sort((a, b) => {
        const av =
          String(a.limit).toLowerCase() === 'unlimited'
            ? Infinity
            : Number(a.limit || 0)
        const bv =
          String(b.limit).toLowerCase() === 'unlimited'
            ? Infinity
            : Number(b.limit || 0)
        if (av !== bv) return av - bv
        return String(a.role || '').localeCompare(String(b.role || ''))
      })

      const rows = merged.map((r, i) => ({
        role: String(r.role || '').trim(),
        limit: r.limit,
        order: i,
        isActive: true
      }))

      const sess = (() => {
        try {
          return JSON.parse(localStorage.getItem('session') || 'null')
        } catch {
          return null
        }
      })()
      const actor = {
        email: sess?.email || '',
        displayName: sess?.displayName || ''
      }
      await replaceAllApprovalLimitsFS(rows, actor)

      await loadLiveLimits()
      setPubMsg('Approval limits published.')
      setStaged([])
    } catch (e) {
      setPubMsg(e.message || 'Publish failed.')
    } finally {
      setPublishing(false)
    }
  }

  function beginEdit(row) {
    setEditingId(row.id)
    setEditingLimit(String(row.limit))
  }

  function cancelEdit() {
    setEditingId('')
    setEditingLimit('')
  }

  async function saveEdit(row) {
    const raw = editingLimit.trim()
    if (!raw) return
    const isUnlimited = raw.toLowerCase() === 'unlimited'
    const parsed = isUnlimited ? 'unlimited' : Number(raw.replace(/[, ]+/g, ''))
    if (!isUnlimited && (isNaN(parsed) || parsed < 0)) return
    const updated = (liveLimits || []).map(r =>
      r.id === row.id ? { ...r, limit: parsed } : r
    )
    const active = updated.filter(r => r.isActive !== false)
    active.sort((a, b) => {
      const av =
        String(a.limit).toLowerCase() === 'unlimited'
          ? Infinity
          : Number(a.limit || 0)
      const bv =
        String(b.limit).toLowerCase() === 'unlimited'
          ? Infinity
          : Number(b.limit || 0)
      if (av !== bv) return av - bv
      return String(a.role || '').localeCompare(String(b.role || ''))
    })

    const batch = writeBatch(db)
    active.forEach((r, i) => {
      const ref = doc(db, 'approvalLimits', r.id)
      const payload = { order: i }
      if (r.id === row.id) {
        payload.limit = parsed
        payload.updatedAt = serverTimestamp()
      }
      batch.update(ref, payload)
    })
    await batch.commit()
    setEditingId('')
    await loadLiveLimits()
  }

  async function deleteRow(row) {
    await updateDoc(doc(db, 'approvalLimits', row.id), {
      isActive: false,
      updatedAt: serverTimestamp()
    })
    await loadLiveLimits()
  }

  const liveSorted = [...liveLimits]
    .filter(r => r.isActive)
    .sort((a, b) => {
      const av =
        String(a.limit).toLowerCase() === 'unlimited'
          ? Infinity
          : Number(a.limit || 0)
      const bv =
        String(b.limit).toLowerCase() === 'unlimited'
          ? Infinity
          : Number(b.limit || 0)
      return av - bv
    })

  // ---------- Document/File Types ----------
  async function loadDocTypes() {
    setDtErr('')
    setDtLoading(true)
    try {
      const rows = await listDocTypesFS()
      setDocTypes(rows)
    } catch (e) {
      console.error(e)
      setDtErr(e.message || 'Failed to load document/file types.')
    } finally {
      setDtLoading(false)
    }
  }

  async function onAddDocType(e) {
    e?.preventDefault?.()
    const t = (newDocType || '').trim()
    if (!t) return
    try {
      await addDocTypeFS(t)
      setNewDocType('')
      await loadDocTypes()
    } catch (e) {
      setDtErr(e.message || 'Failed to add document/file type.')
    }
  }

  function askDeleteDocType(row) {
    setConfirm({ open: true, kind: 'dt-delete', id: row.id, label: row.name })
  }

  function askRestoreDocType(row) {
    setConfirm({ open: true, kind: 'dt-restore', id: row.id, label: row.name })
  }

  // ---------- Entrata PM Sync actions ----------

  async function runPmEntrataSyncNow() {
    setPmSyncRunning(true)
    setPmSyncMsg('Starting PM → Entrata sync…')

    try {
      const url =
        import.meta.env.VITE_PM_ENTRATA_MANUAL_URL ||
        'https://us-central1-low-maintenance.cloudfunctions.net/pmEntrataManualSync'

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          maxInstances: 10 // keep small for manual tests
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok || data?.ok === false) {
        const msg =
          (data && (data.error || data.message)) ||
          `HTTP ${res.status} from pmEntrataManualSync`
        throw new Error(msg)
      }

      const processed = data?.processed ?? data?.total ?? 0
      const failed = data?.failed ?? 0
      const succeeded = data?.succeeded ?? (processed - failed)

      setPmSyncMsg(
        `Sync complete. Processed ${processed} instance(s): ${succeeded} succeeded, ${failed} failed.`
      )

      // Refresh logs after a successful run (if user has them open)
      if (pmSyncLogsOpen) {
        await loadPmSyncLogs()
      }
    } catch (err) {
      console.error('Manual PM Entrata sync failed:', err)
      const msg =
        (err && typeof err.message === 'string' && err.message) ||
        (typeof err === 'string' && err) ||
        'Manual sync failed.'
      setPmSyncMsg(msg)
    } finally {
      setPmSyncRunning(false)
    }
  }

  async function loadPmSyncLogs() {
    setPmSyncLogsError('')
    setPmSyncLogsLoading(true)
    try {
      const url =
        import.meta.env.VITE_PM_ENTRATA_LOGS_URL ||
        'https://us-central1-low-maintenance.cloudfunctions.net/pmEntrataSyncLogs'

      const res = await fetch(url)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          `HTTP ${res.status} from pmEntrataSyncLogs`
        throw new Error(msg)
      }

      // Cloud Function returns { ok: true, rows: [...] }
      const rows = Array.isArray(data?.rows) ? data.rows : []
      setPmSyncLogs(rows)
    } catch (e) {
      console.error('Failed to load PM Entrata sync logs:', e)
      const msg =
        (e && typeof e.message === 'string' && e.message) ||
        (typeof e === 'string' && e) ||
        'Failed to load logs.'
      setPmSyncLogsError(msg)
    } finally {
      setPmSyncLogsLoading(false)
    }
  }

  function handleTogglePmSyncLogs() {
    const opening = !pmSyncLogsOpen
    setPmSyncLogsOpen(opening)
    if (opening) {
      loadPmSyncLogs()
    }
  }

  // ---------- Confirm ----------
  async function onConfirm() {
    try {
      if (confirm.kind === 'ft-delete') {
        await softDeleteFacilityType(confirm.id)
      } else if (confirm.kind === 'ft-restore') {
        await restoreFacilityType(confirm.id)
      } else if (confirm.kind === 'dt-delete') {
        await softDeleteDocTypeFS(confirm.id)
      } else if (confirm.kind === 'dt-restore') {
        await restoreDocTypeFS(confirm.id)
      }
      await Promise.all([loadFacilityTypes(), loadDocTypes()])
    } catch (e) {
      alert(e.message || 'Operation failed.')
    } finally {
      setConfirm({ open: false, kind: null, id: '', label: '' })
    }
  }

  function onCancel() {
    setConfirm({ open: false, kind: null, id: '', label: '' })
  }

  // ---------- Effects ----------
  React.useEffect(() => {
    loadFacilityTypes()
    loadLiveLimits()
    loadRoleOptions()
    loadDocTypes()
  }, [])

  React.useEffect(() => {
    return subscribeSysConfigFS((next) => {
      setAdminOnlyArchive(next?.restrictions?.adminOnlyArchive === true)
      setShowPmWorkOrderMapping(next?.entrata?.showPmWorkOrderMapping === true)
    })
  }, [])

  const active = types
    .filter(t => t.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))
  const inactive = types
    .filter(t => !t.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))

  const dtActive = docTypes
    .filter(t => t.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))
  const dtInactive = docTypes
    .filter(t => !t.isActive)
    .sort((a, b) => a.name.localeCompare(b.name))

  // ---------- Render ----------
  return (
    <div>
         <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>System Configuration</span>
</h2>
      <AdminBar title="" />
      <IdentitySettings />

      {/* ---------- Approval Limits ---------- */}
      <div className="card">
        <h3>Approval Limits</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          Add one or more rows, then click <strong>Publish Approval Limits</strong>.
          Order is computed by limit (lowest amount first; <em>unlimited</em> highest).
        </p>

        <div className="mobile-stack-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: 8,
            alignItems: 'end',
            marginBottom: 12
          }}
        >
          <div>
            <label>Role / Title</label>
            <select
              value={roleSel}
              onChange={e => setRoleSel(e.target.value)}
            >
              {roleOptions.map(r => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Limit (number or "unlimited")</label>
            <input
              value={limitInput}
              onChange={e => setLimitInput(e.target.value)}
              placeholder='e.g., 5000 or "unlimited"'
            />
          </div>
          <div>
            <button className="btn primary" onClick={addStaged}>
              + Add
            </button>
          </div>
        </div>

        {staged.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 12,
              marginTop: 8
            }}
          >
            <p className="muted" style={{ margin: 0 }}>
              No staged rows yet.
            </p>
            <button
              className="btn primary"
              onClick={publishApprovalLimits}
              disabled={publishing || staged.length === 0}
              style={{ whiteSpace: 'nowrap' }}
            >
              {publishing ? 'Publishing…' : 'Publish Approval Limits'}
            </button>
            {pubMsg && <span className="muted">{pubMsg}</span>}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginTop: 8
            }}
          >
            <div style={{ flex: 1 }}>
              <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th>Role</th>
                    <th style={{ textAlign: 'right' }}>Limit</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staged.map((r, i) => (
                    <tr key={i}>
                      <td>{r.role}</td>
                      <td style={{ textAlign: 'right' }}>
                        {r.limit === 'unlimited'
                          ? 'Unlimited'
                          : Number(r.limit).toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                          <button
                            className="btn danger"
                            onClick={() =>
                              setStaged(p => p.filter((_, x) => x !== i))
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <button
                className="btn primary"
                onClick={publishApprovalLimits}
                disabled={publishing || staged.length === 0}
                style={{ whiteSpace: 'nowrap' }}
              >
                {publishing ? 'Publishing…' : 'Publish Approval Limits'}
              </button>
              {pubMsg && <span className="muted">{pubMsg}</span>}
            </div>
          </div>
        )}

        <hr style={{ margin: '16px 0', opacity: 0.2 }} />

        <h4>Currently in Firestore</h4>
        {liveSorted.length === 0 ? (
          <p className="muted">No approval limits stored.</p>
        ) : (
          <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Role</th>
                <th style={{ width: '20%', textAlign: 'center' }}>Limit</th>
                <th style={{ width: '10%', textAlign: 'center' }}>Active</th>
                <th style={{ width: '24%', textAlign: 'center' }}>Updated</th>
                <th style={{ width: '16%', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {liveSorted.map(r => {
                const isEditing = editingId === r.id
                return (
                  <tr key={r.id}>
                    <td style={{ verticalAlign: 'middle' }}>{r.role}</td>
                    <td
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}
                    >
                      {isEditing ? (
                        <input
                          value={editingLimit}
                          onChange={e =>
                            setEditingLimit(e.target.value)
                          }
                          style={{
                            maxWidth: 180,
                            textAlign: 'center'
                          }}
                        />
                      ) : String(r.limit).toLowerCase() ===
                        'unlimited' ? (
                        'Unlimited'
                      ) : (
                        Number(r.limit || 0).toLocaleString()
                      )}
                    </td>
                    <td
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}
                    >
                      {r.isActive ? 'Yes' : 'No'}
                    </td>
                    <td
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fmtTS(r.updatedAt) || fmtTS(r.createdAt)}
                    </td>
                    <td
                      style={{
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}
                    >
                      {isEditing ? (
                        <div
                          style={{
                            display: 'inline-flex',
                            gap: 8
                          }}
                        >
                          <button
                            className="btn primary"
                            onClick={() => saveEdit(r)}
                          >
                            Save
                          </button>
                          <button
                            className="btn"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'inline-flex',
                            gap: 8
                          }}
                        >
                          <button
                            className="btn"
                            onClick={() => beginEdit(r)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn danger"
                            onClick={() => deleteRow(r)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        )}
      </div>

      {/* ---------- Facility Types ---------- */}
      <div className="card">
        <h3>Facility Types</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          Stored in Firestore. Inactive types remain for historical CAFs.
        </p>

        <form
          onSubmit={onAddType}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            marginBottom: 8
          }}
        >
          <div style={{ flex: 1 }}>
            <label>New Type</label>
            <input
              type="text"
              placeholder="e.g., Roofing"
              value={newType}
              onChange={e => setNewType(e.target.value)}
            />
          </div>
          <div>
            <button className="btn primary" type="submit">
              + Add
            </button>
          </div>
        </form>

        {ftErr && (
          <div
            style={{
              background: '#ffecec',
              color: '#b00',
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 8
            }}
          >
            {ftErr}
          </div>
        )}

        {ftLoading ? (
          <div className="muted">Loading…</div>
        ) : (
          <>
            <div style={{ marginTop: 8 }}>
              <h4>Active</h4>
              {active.length === 0 ? (
                <p className="muted">No active types.</p>
              ) : (
                <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Created</th>
                      <th>By</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.map(t => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{fmtTS(t.createdAt)}</td>
                        <td>{t.createdByName || t.createdBy || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn danger"
                            onClick={() => askDeleteType(t)}
                          >
                            Soft Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <h4>Inactive</h4>
              {inactive.length === 0 ? (
                <p className="muted">No inactive types.</p>
              ) : (
                <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Deleted</th>
                      <th>By</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactive.map(t => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{fmtTS(t.deletedAt)}</td>
                        <td>{t.deletedByName || t.deletedBy || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button onClick={() => askRestoreType(t)}>
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ---------- Document / File Types ---------- */}
      <div className="card">
        <h3>Document / File Types</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          Managed in Firestore (<code>docTypes</code>). Inactive types
          remain for historical records and can be restored.
        </p>

        <form
          onSubmit={onAddDocType}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            marginBottom: 8
          }}
        >
          <div style={{ flex: 1 }}>
            <label>New Type</label>
            <input
              type="text"
              placeholder="e.g., Warranty, Policy, Permit"
              value={newDocType}
              onChange={e => setNewDocType(e.target.value)}
            />
          </div>
          <div>
            <button className="btn primary" type="submit">
              + Add
            </button>
          </div>
        </form>

        {dtErr && (
          <div
            style={{
              background: '#ffecec',
              color: '#b00',
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 8
            }}
          >
            {dtErr}
          </div>
        )}

        {dtLoading ? (
          <div className="muted">Loading…</div>
        ) : (
          <>
            <div style={{ marginTop: 8 }}>
              <h4>Active</h4>
              {dtActive.length === 0 ? (
                <p className="muted">No active types.</p>
              ) : (
                <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Created</th>
                      <th>By</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dtActive.map(t => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{fmtTS(t.createdAt)}</td>
                        <td>{t.createdByName || t.createdByEmail || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn danger"
                            onClick={() => askDeleteDocType(t)}
                          >
                            Soft Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <h4>Inactive</h4>
              {dtInactive.length === 0 ? (
                <p className="muted">No inactive types.</p>
              ) : (
                <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Deleted</th>
                      <th>By</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dtInactive.map(t => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{fmtTS(t.deletedAt)}</td>
                        <td>{t.deletedByName || t.deletedByEmail || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button onClick={() => askRestoreDocType(t)}>
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ---------- Entrata API Sync ---------- */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Entrata API Sync</h3>

        {/* Top grid: left text/toggles, right sync button + controls */}
        <div className="mobile-stack-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr auto',
            gap: 16,
            alignItems: 'flex-start',
            marginTop: 4
          }}
        >
          {/* Left side: description + toggles */}
          <div>
            <p className="muted" style={{ marginTop: 0 }}>
              Controls visibility of Entrata fields on PM templates and the PM → Entrata sync
              behavior. Use the on-demand sync button for testing.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <input
                  type="checkbox"
                  checked={showPmWorkOrderMapping === true}
                  onChange={handleToggleShowPmWorkOrderMapping}
                />
                Show Entrata Work Order Mapping on PM forms
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <input
                  type="checkbox"
                  checked={cfg?.entrata?.enableAutoPmSync === true}
                  onChange={handleToggleAutoPmSync}
                />
                Enable scheduled PM → Entrata sync
                <span className="muted" style={{ fontSize: 12 }}>
                  (Used by the Cloud Scheduler / cron job.)
                </span>
              </label>
            </div>
          </div>

          {/* Right side: sync button + status + show logs toggle */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 8
            }}
          >
            <button
              type="button"
              className="btn primary"
              onClick={runPmEntrataSyncNow}
              disabled={pmSyncRunning}
            >
              {pmSyncRunning ? 'Running PM → Entrata Sync…' : 'Run PM → Entrata Sync Now'}
            </button>

            {pmSyncMsg && (
              <div
                className="muted"
                style={{
                  maxWidth: 360,
                  textAlign: 'right',
                  fontSize: 12
                }}
              >
                {pmSyncMsg}
              </div>
            )}

            <button
              type="button"
              className="btn"
              onClick={handleTogglePmSyncLogs}
            >
              {pmSyncLogsOpen ? 'Hide Sync Log' : 'Show Sync Log'}
            </button>
          </div>
        </div>


        {/* Log table spans full width below grid */}
        {pmSyncLogsOpen && (
          <div
            style={{
              marginTop: 12,
              borderTop: '1px solid rgba(0,0,0,0.04)', // muted divider
              paddingTop: 10
            }}
          >
            <h4 style={{ marginTop: 0 }}>PM → Entrata Sync Log</h4>

            {pmSyncLogsError && (
              <div
                style={{
                  background: '#ffecec',
                  color: '#b00',
                  padding: '6px 10px',
                  borderRadius: 4,
                  marginBottom: 8
                }}
              >
                {pmSyncLogsError}
              </div>
            )}

            {pmSyncLogsLoading ? (
              <p className="muted" style={{ marginTop: 4 }}>
                Loading logs…
              </p>
            ) : pmSyncLogs.length === 0 ? (
              <p className="muted" style={{ marginTop: 4 }}>
                No sync runs logged yet.
              </p>
            ) : (
              <div className="responsive-table-scroll" tabIndex={0} role="region" aria-label="Scrollable table"><table className="table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Run At</th>
                    <th style={{ width: '10%', textAlign: 'center' }}>
                      Processed
                    </th>
                    <th style={{ width: '10%', textAlign: 'center' }}>
                      Succeeded
                    </th>
                    <th style={{ width: '10%', textAlign: 'center' }}>
                      Failed
                    </th>
                    <th style={{ width: '48%' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pmSyncLogs.map((log, idx) => {
                    const runAt =
                      log.startedAt ||
                      log.completedAt ||
                      log.runAt ||
                      log.at ||
                      log.createdAt ||
                      log.timestamp ||
                      log.ts

                    // ----- Derive metrics from worker-style logs -----
                    const mapping = Array.isArray(log.results?.mapping)
                      ? log.results.mapping
                      : []

                    const entrataResults = Array.isArray(log.results?.entrata)
                      ? log.results.entrata
                      : []

                    // Derived counts
                    const processedFromMapping = Array.isArray(
                      log.results?.mapping
                    )
                      ? log.results.mapping.filter(m => !m.skipped).length
                      : null

                    const failedFromEntrata = Array.isArray(
                      log.results?.entrata
                    )
                      ? log.results.entrata.filter(
                          r => r && (r.ok === false || r.error)
                        ).length
                      : null

                    const succeededFromEntrata = Array.isArray(
                      log.results?.entrata
                    )
                      ? log.results.entrata.filter(
                          r => r && r.ok && !r.error
                        ).length
                      : null

                    // Prefer derived counts, then legacy fields, then 0
                    const processed =
                      processedFromMapping ??
                      log.processed ??
                      log.count ??
                      log.total ??
                      0

                    const failed =
                      failedFromEntrata ??
                      log.failed ??
                      Math.max(
                        0,
                        processed - (log.succeeded ?? 0)
                      )

                    const succeeded =
                      succeededFromEntrata ??
                      log.succeeded ??
                      Math.max(0, processed - failed)

                    // ----- Build a meaningful Notes summary -----
                    const errorMessages = []
                    let hasConnectivityIssue = false

                    entrataResults.forEach(r => {
                      const code = r?.error?.code || r?.error?.meta?.code

                      if (
                        code === 'config/missing-env' ||
                        (typeof code === 'string' &&
                          code.startsWith('network/'))
                      ) {
                        hasConnectivityIssue = true
                      }

                      const msg =
                        r?.error?.message ||
                        r?.error?.meta?.message ||
                        r?.message

                      if (msg && !errorMessages.includes(msg)) {
                        errorMessages.push(msg)
                      }
                    })

                    let noteSummary = ''

                    const unfinished = log.status === 'running'

                    if (unfinished) {
                      noteSummary =
                        'No completion was recorded. The sync may still be running or may have timed out. Counts are unknown; check the run before retrying.'
                    } else if (log.status === 'error') {
                      const errMsg =
                        (log.error && log.error.message) ||
                        (typeof log.error === 'string' && log.error) ||
                        'Cloud Function error.'
                      noteSummary = `Function error: ${errMsg}`
                    } else if (hasConnectivityIssue) {
                      noteSummary =
                        'Entrata connectivity problem (config or network error). See Details for full response.'
                    } else if (processed === 0) {
                      noteSummary =
                        'No PM templates were eligible to sync for this run.'
                    } else if (failed > 0 && errorMessages.length > 0) {
                      noteSummary = `Some PMs failed in Entrata: ${errorMessages.join(
                        ' | '
                      )}`
                    } else if (failed > 0) {
                      noteSummary =
                        'Some PMs failed in Entrata. See Details for error payload.'
                    } else {
                      noteSummary =
                        'All PM templates synced successfully to Entrata.'
                    }

                    // Full JSON for <details>
                    let detailsJson = ''
                    try {
                      detailsJson = JSON.stringify(log, null, 2)
                    } catch {
                      detailsJson = ''
                    }

                    return (
                      <tr key={log.id || idx}>
                        <td>{fmtTS(runAt)}</td>
                        <td style={{ textAlign: 'center' }}>
                          {unfinished ? '—' : processed}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {unfinished ? '—' : succeeded}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {unfinished ? '—' : failed}
                        </td>
                        <td>
                          {noteSummary || '—'}
                          {detailsJson && (
                            <details
                              style={{ fontSize: 11, marginTop: 4 }}
                            >
                              <summary>Details</summary>
                              <pre
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  margin: 0,
                                  maxHeight: 200,
                                  overflow: 'auto'
                                }}
                              >
                                {detailsJson}
                              </pre>
                            </details>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table></div>
            )}

            <div
              className="muted"
              style={{ fontSize: 11, marginTop: 4 }}
            >
              Data is returned by the <code>pmEntrataSyncLogs</code> Cloud
              Function. Each row represents one sync run and its summary.
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Archive Restrictions</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          When enabled, only administrators can archive or soft delete supported records.
          Other users will be prompted to notify an administrator instead.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 8
          }}
        >
          <label style={{ display:'inline-flex', alignItems:'center', gap:8, margin:0 }}>
            <input
              type="checkbox"
              checked={adminOnlyArchive}
              onChange={e => setAdminOnlyArchive(e.target.checked)}
            />
            Restrict archive / soft delete to Administrators
          </label>

          <button
            className="btn primary"
            onClick={saveArchiveRestriction}
            disabled={archiveSaving}
            style={{ height: 40 }}
          >
            {archiveSaving ? 'Saving…' : 'Save'}
          </button>

          {archiveMsg && <span className="muted">{archiveMsg}</span>}
        </div>
      </div>

      {/* ---------- Upload Settings (devStore) ---------- */}
      <div className="card">
        <h3>Uploads</h3>
        <p className="muted" style={{ marginTop: -6 }}>
          Control the maximum file size allowed for PDF uploads in CAFs and
          related documents.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 8
          }}
        >
          <label
            htmlFor="maxPdfMB"
            style={{ margin: 0, whiteSpace: 'nowrap' }}
          >
            Max PDF Size (MB)
          </label>

          <input
            id="maxPdfMB"
            type="number"
            min={1}
            value={cfg?.uploads?.maxPdfMB ?? 25}
            onChange={e =>
              setField('uploads.maxPdfMB', Number(e.target.value))
            }
            style={{ width: 120, height: 40, paddingInline: 12 }}
          />

          <button
            className="btn primary"
            onClick={saveDevCfg}
            disabled={!dirty || cfgSaving}
            style={{ height: 40 }}
          >
            {cfgSaving ? 'Saving…' : 'Save'}
          </button>

          {cfgMsg && <span className="muted">{cfgMsg}</span>}
        </div>

        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Default is 25 MB
        </div>
      </div>

      <ConfirmDialog
        open={confirm.open}
        title={
          confirm.kind === 'ft-delete'
            ? 'Soft delete facility type'
            : confirm.kind === 'ft-restore'
            ? 'Restore facility type'
            : confirm.kind === 'dt-delete'
            ? 'Soft delete document/file type'
            : confirm.kind === 'dt-restore'
            ? 'Restore document/file type'
            : ''
        }
        message={
          confirm.kind === 'ft-delete'
            ? (
              <>
                This hides <strong>{confirm.label}</strong> from future CAFs
                but preserves history.
              </>
              )
            : confirm.kind === 'ft-restore'
            ? (
              <>
                This makes <strong>{confirm.label}</strong> selectable again.
              </>
              )
            : confirm.kind === 'dt-delete'
            ? (
              <>
                This hides <strong>{confirm.label}</strong> from upload
                pickers but preserves history.
              </>
              )
            : confirm.kind === 'dt-restore'
            ? (
              <>
                This makes <strong>{confirm.label}</strong> selectable again.
              </>
              )
            : ''
        }
        confirmLabel={
          confirm.kind === 'ft-delete' || confirm.kind === 'dt-delete'
            ? 'Soft Delete'
            : 'Restore'
        }
        cancelLabel="Cancel"
        tone={
          confirm.kind === 'ft-delete' || confirm.kind === 'dt-delete'
            ? 'danger'
            : 'default'
        }
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  )
}
